import { type Qualification } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import {
  calculateMissingCredential,
  mapPlacementDetailRequirementsToPlacementDataRequirements,
} from "@src/appV2/redesign/Documents/utils/calculateMissingCredential";
import {
  INTERVIEW_TYPE,
  type InterviewType,
  type InterviewWithDetails,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";

import { type OnboardingRequirement } from "../../../../PlacementCandidate/hooks/useOnboardingRequirement";
import {
  type ActiveBookingReorderingCohort,
  type PlacementCandidate,
  PlacementCandidateScreeningStatus,
} from "../../../../PlacementCandidate/types";
import { type InterviewAvailability } from "../../../api/useGetInterviewAvailability";
import { type PlacementDetail } from "../../../api/useGetPlacement";
import { type TalkToRecruiterCopy } from "../../../hooks/useTalkToRecruiterCopyText";
import {
  getUnverifiedBookedInterview,
  hasAppliedToPlacement,
  hasAttendedInterviewWithinRebookWindow,
  hasBookedInterview,
  hasBookedInterviewInFuture,
  hasPendingInterview,
  hasPendingInterviewInFuture,
  interviewRequired,
  isApplicationCompleted,
} from "../../../utils";
import { getActionParamsFromConnectedListingPlacementAndInterview } from "../../../utils/connected-listings";
import { getVerifiedType } from "../../../utils/verified-placement";
import { type PrimaryActionParams } from "./getPrimaryAction";

export interface BuildPrimaryActionParamsOptions {
  isLoadingInterviewAvailability: boolean;
  isProfileCompleted: boolean;
  isProfilePending: boolean;
  isProfileInvalid: boolean;
  requirement?: OnboardingRequirement;
  talkToRecruiterCopy: TalkToRecruiterCopy;
  isJobInterviewsEnabled: boolean;
  isJobRequestEnabled: boolean;
  isHiringReferralEnabled: boolean;
  maxReferralBonusAmount: number;
  isOnboardingRequirementsLoading: boolean;
  isGetIncentivesLoading: boolean;
  isReschedulingPastInterview: boolean;
  startsInWorkplaceToday?: boolean;
  bookedInterviewType?: InterviewType;
  isChildcareSupportChatEnabled: boolean;
  isSubmitHiringDocumentsLaterEnabled: boolean;
  autoApprovedQualifications: Set<string>;
  workerShoppableQualificationIds: string[];
  allQualifications: Qualification[];
  isQualificationsLoading: boolean;
  bookingReorderingCohort?: ActiveBookingReorderingCohort;
  hasInProgressInterview: boolean;
  hasWorkerManualEta: boolean;
  isEasySchedulingPlacement: boolean;
  interviewFromConnectedListing?: InterviewWithDetails;
  isInterviewFromConnectedListingLoading: boolean;
  /** Whether screen_last cohort can bypass document requirements */
  canBypassDocumentsForScreenLast: boolean;
}

/**
 * Find the first placement worker type that:
 * 1. Is in the auto-approved qualifications list (from feature flag)
 * 2. Worker doesn't already have it in their shoppable qualifications
 *
 * Returns the qualification name, ID, and display label if found.
 */
function findMissingAutoApprovedQualification(
  placementWorkerTypes: string[] | undefined,
  workerShoppableQualificationIds: string[],
  autoApprovedQualifications: Set<string>,
  allQualifications: Qualification[]
): { name?: string; id?: string; displayLabel?: string } {
  if (!placementWorkerTypes || placementWorkerTypes.length === 0) {
    return {};
  }

  // Find first placement worker type that is auto-approved and worker doesn't have
  for (const workerType of placementWorkerTypes) {
    if (autoApprovedQualifications.has(workerType)) {
      // Find this qualification in all qualifications to get ID and display label
      const qualification = allQualifications.find((q) => q.attributes.name === workerType);
      if (qualification && !workerShoppableQualificationIds.includes(qualification.id)) {
        return {
          name: workerType,
          id: qualification.id,
          displayLabel: qualification.attributes.displayLabel,
        };
      }
    }
  }

  return {};
}

export function buildPrimaryActionParams(
  placementDetails: PlacementDetail,
  placementCandidate: PlacementCandidate,
  interviewAvailability: InterviewAvailability,
  options: BuildPrimaryActionParamsOptions
): PrimaryActionParams {
  const {
    isLoadingInterviewAvailability,
    isProfileCompleted,
    isProfilePending,
    isProfileInvalid,
    requirement,
    talkToRecruiterCopy,
    isJobInterviewsEnabled,
    isJobRequestEnabled,
    isHiringReferralEnabled,
    maxReferralBonusAmount,
    isOnboardingRequirementsLoading,
    isGetIncentivesLoading,
    isReschedulingPastInterview,
    startsInWorkplaceToday,
    bookedInterviewType,
    isChildcareSupportChatEnabled,
    isSubmitHiringDocumentsLaterEnabled,
    autoApprovedQualifications,
    workerShoppableQualificationIds,
    allQualifications,
    isQualificationsLoading,
    bookingReorderingCohort,
    hasInProgressInterview,
    hasWorkerManualEta,
    isEasySchedulingPlacement,
    interviewFromConnectedListing,
    isInterviewFromConnectedListingLoading,
    canBypassDocumentsForScreenLast,
  } = options;

  const { credentialRequirements = [] } = placementDetails;

  // Derive placement state
  const verifiedType = getVerifiedType(placementDetails);
  const requiredInterview = interviewRequired(placementDetails);
  const bookedInterview = hasBookedInterview(placementDetails);
  const pendingInterview = hasPendingInterview(placementDetails);
  const bookedInterviewInFuture = hasBookedInterviewInFuture(placementDetails);
  const pendingInterviewInFuture = hasPendingInterviewInFuture(placementDetails);
  const attendedInterview = hasAttendedInterviewWithinRebookWindow(placementDetails);
  const applicationCompleted = isApplicationCompleted(placementDetails);
  const isWaitlist = placementDetails.status === "waitlist";
  const hasApplied = hasAppliedToPlacement(placementDetails);
  const unverifiedBookedInterview = getUnverifiedBookedInterview(placementDetails);

  // Use the actual booked interview type if available, otherwise fall back to workplace settings
  const selfServeInterviewBookingType =
    bookedInterviewType ??
    placementDetails.interviewSettings?.attributes.selfServeInterviewBookingType ??
    INTERVIEW_TYPE.IN_PERSON;

  // Derive document requirements state
  const missingDocuments = placementDetails.workerRequirements?.filter(
    (request) => !request.attributes.isValid
  );
  const hasOnlyInvisibleHCPInvalidDocuments =
    isDefined(missingDocuments) &&
    missingDocuments.length > 0 &&
    missingDocuments.every(
      (request) => !request.relationships.requirement.data.attributes.visibleToHCP
    );

  // Derive candidate screening state
  const screeningStatus =
    placementCandidate.screeningStatus ?? PlacementCandidateScreeningStatus.NO_SCREENING;
  const isExcellentCandidate =
    screeningStatus === PlacementCandidateScreeningStatus.PASSED_EXCEPTIONALLY;

  // Derive loading state
  const isLoading =
    isLoadingInterviewAvailability ||
    isOnboardingRequirementsLoading ||
    isGetIncentivesLoading ||
    isQualificationsLoading ||
    isInterviewFromConnectedListingLoading;

  // Find missing auto-approved qualification
  const missingAutoApprovedQualification = findMissingAutoApprovedQualification(
    placementDetails.workerTypes ?? [],
    workerShoppableQualificationIds,
    autoApprovedQualifications,
    allQualifications
  );

  const missingCredentialRequirementType = calculateMissingCredential(
    mapPlacementDetailRequirementsToPlacementDataRequirements(credentialRequirements)
  );

  const {
    hasConnectedListingInterview,
    connectedListingInterviewStartTime,
    connectedListingHiringManagerName,
    isConnectedListingInterviewInOtherWorkplace,
    connectedListingInterviewWorkplaceName,
  } = getActionParamsFromConnectedListingPlacementAndInterview(
    placementDetails,
    interviewFromConnectedListing
  );

  return {
    hasApplied,
    hasInterview: bookedInterview,
    hasInterviewInFuture: bookedInterviewInFuture,
    hasPendingInterviewInFuture: pendingInterviewInFuture,
    applicationCompleted,
    hasInterviewAvailability: interviewAvailability.length > 0,
    isLoadingInterviewAvailability,
    isProfileComplete: isProfileCompleted,
    isProfilePending,
    isProfileInvalid,
    requirementDisplayName: requirement?.getDisplayName() ?? "profile",
    requirementSubItemsCount: requirement?.subItems?.length ?? 0,
    hasRequiredLicenses: placementDetails.isLicenseEligible,
    workplaceState: placementDetails.workplace.state,
    hasRequiredDocuments:
      !isDefined(placementDetails.isDocumentRequirementValid) ||
      placementDetails.isDocumentRequirementValid,
    hasOnlyInvisibleHCPInvalidDocuments,
    isExemptFromDocumentRequirements: placementDetails.isExemptFromDocumentRequirements ?? false,
    isDocumentRequirementValid: placementDetails.isDocumentRequirementValid,
    bookedInterview,
    pendingInterview,
    attendedInterview,
    isProspectWorkplace: Boolean(placementDetails.workplace.placementSettings?.isProspect),
    requiredInterview,
    screeningStatus,
    isRecruiterScreeningEnabled: placementDetails.isRecruiterScreeningEnabled ?? false,
    talkToRecruiterLabel: talkToRecruiterCopy.placementAction.screening,
    talkToRecruiterDescription: talkToRecruiterCopy.placementAction.screeningDescription,
    talkToRecruiterUpskillingLabel: talkToRecruiterCopy.placementAction.upskilling,
    talkToRecruiterUpskillingDescription: talkToRecruiterCopy.placementAction.upskillingDescription,
    rescheduleRecruiterLabel: "Reschedule recruiter call",
    isJobInterviewsEnabled,
    isJobRequestEnabled,
    verifiedType,
    isWaitlist,
    isRestricted: placementCandidate.isRestricted ?? false,
    isHiringReferralEnabled,
    maxReferralBonusAmount,
    hasWebApplicationLink: Boolean(placementDetails.webApplicationLink),
    isExcellentCandidate,
    isLoading,
    isReschedulingPastInterview,
    startsInWorkplaceToday,
    bookingReorderingCohort,
    selfServeInterviewBookingType,
    isChildcareSupportChatEnabled,
    isSubmitHiringDocumentsLaterEnabled,
    missingAutoApprovedQualificationName: missingAutoApprovedQualification.name,
    missingAutoApprovedQualificationDisplayLabel: missingAutoApprovedQualification.displayLabel,
    missingAutoApprovedQualificationId: missingAutoApprovedQualification.id,
    missingCredentialRequirementType,
    hasUnverifiedBookedInterview: isDefined(unverifiedBookedInterview),
    hasInitiatedInterviewVerifyAttendance: isDefined(
      unverifiedBookedInterview?.relationships.interviewAttendanceVerification
    ),
    hasInProgressInterview,
    hasWorkerManualEta,
    isEasySchedulingPlacement,
    hasConnectedListingInterview,
    connectedListingInterviewStartTime,
    connectedListingHiringManagerName,
    isConnectedListingInterviewInOtherWorkplace,
    connectedListingInterviewWorkplaceName,
    canBypassDocumentsForScreenLast,
  };
}
